import { ApolloError } from '@apollo/client';
import React, { CSSProperties } from 'react';
import { Divider, makeStyles, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IDocument } from '../../../utils/statApi/DocumentApi';
import DocumentView from './DocumentView';

const useStyles = makeStyles({
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '12px 0',
  },
});

interface IDocumentCardListProps {
  docType: string;
  documents: IDocument[];
  loading: boolean;
  showDeletedFiles: boolean;
  error?: ApolloError;
}

const DocumentCardList = ({
  docType,
  documents,
  loading,
  showDeletedFiles,
  error,
}: IDocumentCardListProps): JSX.Element => {
  const styles = useStyles();

  if (error) {
    return <></>;
  }

  const recycledDocs = documents?.filter((doc: IDocument) => doc.isDeleted) || [];

  const h4Style = { marginBottom: 0 } as CSSProperties;
  const h4TopStyle = { margin: 0 } as CSSProperties;

  return (
    <Stack>
      {showDeletedFiles && (
        <>
          <div>
            <h4 style={h4TopStyle}>Recycled Documents</h4>
            <Divider />
          </div>
          <Stack horizontal wrap tokens={{ childrenGap: 20 }}>
            {recycledDocs.length === 0 && <span>No recycled documents found.</span>}
            {recycledDocs.map((document, index): JSX.Element => {
              return <DocumentView document={document} index={index} />;
            })}
          </Stack>
        </>
      )}
      {showDeletedFiles && (
        <div>
          <h4 style={h4Style}>{`${docType} Documents`}</h4>
          <Divider />
        </div>
      )}

      {loading && (
        <Skeleton aria-label="Loading documents" className={styles.skeleton}>
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
          <SkeletonItem />
        </Skeleton>
      )}

      {!loading && documents.length === 0 && (
        <p>No documents found. Try changing the filters or uploading documents.</p>
      )}

      {!loading && documents.length > 0 && (
        <Stack horizontal wrap tokens={{ childrenGap: 20 }}>
          {documents
            .filter((doc: IDocument) => !doc.isDeleted)
            .map((document: IDocument, index: number): JSX.Element => {
              return <DocumentView document={document} index={index} />;
            })}
        </Stack>
      )}
    </Stack>
  );
};

export default DocumentCardList;
